import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sparkles } from "lucide-react";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import {
  BUSINESS_TYPE_LIST,
  DEFAULT_BUSINESS_TYPE,
  resolveBusinessConfig,
} from "@/config/businessTypes";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialType = params.get("business") || DEFAULT_BUSINESS_TYPE;

  const [form, setForm] = useState({
    name: "",
    business_name: "",
    email: "",
    whatsapp: "",
    password: "",
    business_type: BUSINESS_TYPE_LIST.some((b) => b.id === initialType)
      ? initialType
      : DEFAULT_BUSINESS_TYPE,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const cfg = useMemo(() => resolveBusinessConfig(form.business_type), [form.business_type]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const payload = {
      ...form,
      default_services: cfg.defaultServices, // niche presets, backend seeds them
    };
    const res = await register(payload);
    setLoading(false);
    if (res.ok) {
      toast.success("¡Cuenta creada! Configura tus servicios.");
      navigate("/dashboard/settings");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-void text-neutral-100 grain flex flex-col">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-3 mb-8 justify-center" data-testid="brand-link">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold to-neonpink flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-void" strokeWidth={2} />
            </div>
            <span className="font-display text-3xl">AgendaPro</span>
          </Link>

          <div className="bg-surface gold-border rounded-2xl p-8 animate-fade-up">
            <p className="overline mb-2">Crea tu negocio</p>
            <h1 className="font-display text-3xl mb-6">Regístrate</h1>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-neutral-300 text-xs uppercase tracking-widest">
                  Tipo de negocio
                </Label>
                <Select
                  value={form.business_type}
                  onValueChange={(v) => setForm({ ...form, business_type: v })}
                >
                  <SelectTrigger
                    className="mt-2 bg-void border-gold/20 focus:border-cyan h-12"
                    data-testid="register-business-type"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-gold/20">
                    {BUSINESS_TYPE_LIST.map((b) => (
                      <SelectItem key={b.id} value={b.id} data-testid={`bt-option-${b.id}`}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {[
                { key: "name", label: "Tu nombre", type: "text", ph: "Tu nombre", tid: "register-name" },
                { key: "business_name", label: "Nombre del negocio", type: "text", ph: "Mi Negocio", tid: "register-business" },
                { key: "email", label: "Correo", type: "email", ph: "tu@correo.com", tid: "register-email" },
                { key: "whatsapp", label: "WhatsApp con lada", type: "tel", ph: "+527297553698", tid: "register-whatsapp" },
                { key: "password", label: "Contraseña", type: "password", ph: "Mín 6 caracteres", tid: "register-password" },
              ].map((f) => (
                <div key={f.key}>
                  <Label htmlFor={f.key} className="text-neutral-300 text-xs uppercase tracking-widest">
                    {f.label}
                  </Label>
                  <Input
                    id={f.key}
                    type={f.type}
                    value={form[f.key]}
                    onChange={update(f.key)}
                    placeholder={f.ph}
                    required
                    data-testid={`${f.tid}-input`}
                    className="mt-2 bg-void border-gold/20 focus:border-cyan focus-visible:ring-1 focus-visible:ring-cyan h-12"
                  />
                </div>
              ))}

              {error && (
                <div data-testid="register-error" className="text-neonpink text-sm">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                data-testid="register-submit-btn"
                className="w-full bg-gold text-void hover:bg-gold/90 rounded-full font-bold h-12 mt-2"
              >
                {loading ? "Creando…" : "Crear cuenta"}
              </Button>
              <div className="text-center pt-2" data-testid="register-trial-note">
                <p className="font-display text-lg leading-tight">Empieza hoy.</p>
                <p className="text-neutral-400 text-sm mt-1 leading-relaxed">
                  Disfruta AgendaPro PRO durante 10 días sin restricciones.
                </p>
              </div>
            </form>

            <div className="mt-6 text-center text-sm text-neutral-400">
              ¿Ya tienes cuenta?{" "}
              <Link to="/login" className="text-gold hover:underline" data-testid="go-login-link">
                Iniciar sesión
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
